import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import {
  makeStyles,
  Typography,
  IconButton,
  Dialog,
  DialogTitle as MuiDialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import { OutlinedTextArea, OutlinedTextField } from '../common/fields';
import FilePicker from '../common/filePicker';

const useStyles = makeStyles((theme) => ({
  dialog: {
    height: '60%',
    width: '60%',
    maxWidth: 'none',
    [theme.breakpoints.down('sm')]: {
      height: '100%',
      width: '100%',
      maxWidth: 'none',
      maxHeight: 'none',
      margin: 0
    },
  },
  actions: {
    '& > *': {
      margin: theme.spacing(0, 1)
    },
    '& > *:first-child': {
      marginLeft: 0
    },
    '& > *:last-child': {
      marginRight: 0
    }
  },
  image: {
    width: '100px',
    height: '56px',
    objectFit: 'contain',
  },
}));

const dialogTitleStyles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
});

const DialogTitle = withStyles(dialogTitleStyles)((props) => {
  const { children, classes, onClose, ...rest } = props;
  const { t } = useTranslation();

  return (
    <MuiDialogTitle disableTypography className={classes.root} {...rest}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton aria-label={t('common:close')} title={t('common:close')} className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const LanguageContent = ({award, lang, handleChange, forwardRef}) => {
  const { t } = useTranslation();

  return <div ref={forwardRef}>
    <Box mb={3}>
      <Typography variant="h6" component="h2">{lang.name} ({lang.id})</Typography></Box>
    <Box my={3}>
      <OutlinedTextField
        label={t('tyreAwards:awardName')}
        name={'award'}
        value={award.award || ''}
        onChange={(e) => handleChange({...award, 'award': e.target.value})}
        disabled={false}
        maxLength={25}
        helperText={t('tyreAwards:awardNameHelper')}
      />
    </Box>
    <Box my={3}>
      <OutlinedTextArea
        label={t('tyreAwards:reviewInfo')}
        name={'review'}
        value={award.review || ''}
        onChange={(e) => handleChange({...award, 'review': e.target.value})}
        disabled={false}
        maxLength={500}
        helperText={t('tyreAwards:reviewHelper')}
      />
    </Box>
    <Box my={3}>
      <OutlinedTextArea
        label={t('tyreAwards:highlight')}
        name={'highlight'}
        value={award.highlight || ''}
        onChange={(e) => handleChange({...award, 'highlight': e.target.value})}
        disabled={false}
        maxLength={500}
        helperText={t('tyreAwards:highlightHelper')}
      />
    </Box>
    <Box my={3}>
      <FilePicker fieldName={t('tyreAwards:overrideLogo')}
        fieldValue={award.logoFile}
        rootFolderId={'52c14f5f2b089fc2b66b98e7'}
        setFieldValue={(file) => handleChange({...award, 'logoFile': file})}
        disabled={false} />
    </Box>
  </div>
}

LanguageContent.propTypes = {
  award: PropTypes.shape({
    award: PropTypes.string,
    review: PropTypes.string,
    highlight: PropTypes.string,
    logoFile: PropTypes.object
  }),
  lang: PropTypes.object,
  handleChange: PropTypes.func,
  forwardRef: PropTypes.any
}

const TranslationDialog = (props) => {
  const [ tyreAwards, setTyreAwards ] = useState(props.awards);
  const { t } = useTranslation();
  const classes = useStyles();
  const [refs, setRefs] = useState({});

  const handleChange = (value, lang) => {
    setTyreAwards({...tyreAwards, [lang]: value});
  }

  useEffect(() => {
    if (props.awards) {
      setTyreAwards(props.awards)
    }
  }, [props.awards])

  useEffect(() => {
    if (props.awards) {
      setRefs(Object.keys(props.awards).reduce((acc, value) => {
        acc[value] = React.createRef();
        return acc;
      }, {}));
    }
  }, [props.open, props.country])

  useEffect(() => {
    if (props.open && props.country && refs[props.country.id]) {
      refs[props.country.id]?.current ? refs[props.country.id].current.scrollIntoView() : null;
    }
  }, [props.open, props.country, refs])

  const onSave = _ => {
    props.handleSave(tyreAwards);
  }

  return (
    <Dialog onClose={props.handleClose} aria-labelledby="dialog-title" open={props.open} classes={{ paper: classes.dialog}}>
      <DialogTitle id="dialog-title" onClose={props.handleClose}>
        {t('tyreAwards:editTranslation')}
      </DialogTitle>
      <DialogContent dividers>
        {props.countries && tyreAwards && props.countries.map(lang => (
          <LanguageContent forwardRef={refs[lang.id]}
            key={`award-${lang.id}`}
            lang={lang}
            award={tyreAwards[lang.id]}
            handleChange={(val) => handleChange(val, lang.id)} />
        ))}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button variant={'outlined'} onClick={props.handleClose} color="primary">
          {t('common:cancelChanges')}
        </Button>
        <Button variant={'contained'} onClick={onSave} color="primary">
          {t('common:save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

TranslationDialog.propTypes = {
  open: PropTypes.bool,
  awards: PropTypes.object,
  country: PropTypes.object,
  countries: PropTypes.arrayOf(PropTypes.object),
  handleSave: PropTypes.func,
  handleClose: PropTypes.func,
}

export default TranslationDialog;
